import * as React from "react";
import { useEffect, useState } from "react";
import axios from "axios";
import { useHistory, useParams } from "react-router-dom";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { Notification, useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPatch } from "../getConfig";
import RecordFields from "../layout/RecordFields";
import { approveInvoice } from "./approve";
import { brandLabel, useBrandNames } from "./brands";
import {
  DataTable,
  LocalPagination,
  PageHeader,
  SectionHeader,
  StatusChip,
  bpsToPct,
  errorMessage,
  fmtDate,
  fmtDateTime,
  fmtPeriod,
  gbp,
  humanize,
  useCloStyles,
} from "./shared";

const LINE_PAGE_SIZES = [50, 100, 250];

const LINE_COLUMNS = [
  { label: "Reward" },
  { label: "User" },
  { label: "Campaign" },
  { label: "Network" },
  { label: "ARN" },
  { label: "Transaction" },
  { label: "Cleared" },
  { label: "Net", numeric: true },
  { label: "Reward amount", numeric: true },
  { label: "Fee", numeric: true },
  { label: "Zero reason" },
  { label: "Status" },
];

const InvoiceShow = () => {
  const classes = useCloStyles();
  const notify = useNotify();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [invoice, setInvoice] = useState<any>(null);
  const [markOpen, setMarkOpen] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [marking, setMarking] = useState(false);
  const [linePage, setLinePage] = useState(0);
  const [linePageSize, setLinePageSize] = useState(LINE_PAGE_SIZES[0]);
  const names = useBrandNames([invoice?.brand_id]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const res: any = await axios.get(`${ENDPOINTS.CLO_INVOICES}/${id}`, getConfigGet({}));
      setInvoice(res.data);
    } catch (err) {
      notify(errorMessage(err, "Failed to load invoice"), "warning");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoice();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async () => {
    try {
      setApproving(true);
      const result = await approveInvoice(invoice, brandLabel(names, invoice.brand_id));
      if (!result) return;
      notify(`Approved invoices for ${brandLabel(names, result.brandId)}, ${fmtPeriod(result.period)}`, "info");
      fetchInvoice();
    } catch (err) {
      notify(errorMessage(err, "Failed to approve invoice"), "warning");
    } finally {
      setApproving(false);
    }
  };

  const markSent = async () => {
    const number = invoiceNumber.trim();
    if (!number || number.length > 64) {
      notify("Invoice number must be 1–64 characters", "warning");
      return;
    }
    try {
      setMarking(true);
      const res: any = await axios.patch(
        `${ENDPOINTS.CLO_INVOICES}/${invoice.id}`,
        { action: "mark_sent", externalInvoiceNumber: number },
        { headers: getConfigPatch() }
      );
      setInvoice(res.data);
      setMarkOpen(false);
      notify(`Invoice marked sent as ${number}`, "info");
    } catch (err) {
      notify(errorMessage(err, "Failed to mark invoice sent"), "warning");
    } finally {
      setMarking(false);
    }
  };

  const back = (
    <Button startIcon={<ArrowBackIcon />} onClick={() => history.push("/clo/billing?tab=invoices")}>Invoices</Button>
  );

  if (loading && !invoice) return <div className={classes.loadingContainer}><CircularProgress size={40} /></div>;
  if (!invoice) {
    return (
      <div className={classes.main}>
        <PageHeader title="Invoice not found" actions={back} />
        <Notification />
      </div>
    );
  }

  const lines: any[] = invoice.lines || [];
  const zeroLines = lines.filter((l) => l.zero_reason).length;
  const pageLines = lines.slice(linePage * linePageSize, (linePage + 1) * linePageSize);

  const summary: { label: string; value: any }[] = [
    { label: "Brand", value: brandLabel(names, invoice.brand_id) },
    { label: "Period", value: fmtPeriod(invoice.period) },
    { label: "Part", value: invoice.part },
    { label: "Status", value: invoice.status },
    { label: "Issued", value: fmtDate(invoice.issued_on) },
    { label: "Due", value: fmtDate(invoice.due_on) },
    { label: "Lines", value: invoice.line_count },
    { label: "Subtotal", value: gbp(invoice.subtotal_minor) },
    { label: "VAT rate", value: bpsToPct(invoice.vat_bps) },
    { label: "VAT", value: gbp(invoice.vat_minor) },
    { label: "Total", value: gbp(invoice.total_minor) },
    { label: "Finance invoice no.", value: invoice.external_invoice_number },
    { label: "Approved by", value: invoice.approved_by },
    { label: "Approved at", value: invoice.approved_at ? fmtDateTime(invoice.approved_at) : null },
    { label: "Sent at", value: invoice.sent_at ? fmtDateTime(invoice.sent_at) : null },
  ];

  return (
    <div className={classes.main}>
      <PageHeader
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 12 }}>
            Invoice {invoice.external_invoice_number || `#${invoice.id}`}
            <StatusChip status={invoice.status} />
          </span>
        }
        subtitle={`${brandLabel(names, invoice.brand_id)} · ${fmtPeriod(invoice.period)} · ${invoice.part}`}
        actions={
          <>
            {back}
            <Button
              variant="outlined"
              color="primary"
              onClick={() =>
                history.push(
                  `/clo/billing?tab=payments&brandId=${encodeURIComponent(invoice.brand_id)}&period=${encodeURIComponent(invoice.period)}`
                )
              }
            >
              Record payment
            </Button>
            {invoice.status === "approved" && (
              <Button
                variant="contained"
                color="primary"
                disableElevation
                onClick={() => {
                  setInvoiceNumber(invoice.external_invoice_number || "");
                  setMarkOpen(true);
                }}
              >
                Mark sent
              </Button>
            )}
            {invoice.status === "draft" && (
              <Button variant="contained" color="primary" disableElevation disabled={approving} onClick={approve}>
                {approving ? <CircularProgress size={20} /> : "Approve"}
              </Button>
            )}
          </>
        }
      />

      <RecordFields labels={summary.map((f) => f.label)} values={summary.map((f) => f.value)} />

      <SectionHeader
        title="Lines annex"
        meta={`${lines.length} lines${zeroLines ? `, ${zeroLines} at £0` : ""}`}
      />

      <DataTable
        columns={LINE_COLUMNS}
        isEmpty={lines.length === 0}
        empty="This invoice has no lines."
        footer={
          lines.length > LINE_PAGE_SIZES[0] ? (
            <LocalPagination
              count={lines.length}
              page={linePage}
              pageSize={linePageSize}
              sizes={LINE_PAGE_SIZES}
              onPage={setLinePage}
              onPageSize={(size) => {
                setLinePageSize(size);
                setLinePage(0);
              }}
            />
          ) : undefined
        }
      >
        {pageLines.map((l) => (
          <TableRow key={l.id} hover>
            <TableCell>{l.reward_id}</TableCell>
            <TableCell>{l.user_id}</TableCell>
            <TableCell>{l.campaign_id}</TableCell>
            <TableCell>{l.network}</TableCell>
            <TableCell>{l.arn || "—"}</TableCell>
            <TableCell>{fmtDateTime(l.transaction_at)}</TableCell>
            <TableCell>{fmtDateTime(l.cleared_at)}</TableCell>
            <TableCell align="right">{gbp(l.net_minor)}</TableCell>
            <TableCell align="right">{gbp(l.reward_minor)}</TableCell>
            <TableCell align="right">{gbp(l.fee_minor)}</TableCell>
            <TableCell>
              {l.zero_reason ? <Typography variant="body2" className={classes.muted}>{l.zero_reason}</Typography> : "—"}
            </TableCell>
            <TableCell>{l.status}</TableCell>
          </TableRow>
        ))}
      </DataTable>
      <Dialog open={markOpen} onClose={() => !marking && setMarkOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Mark invoice sent</DialogTitle>
        <DialogContent>
          <div className={classes.dialogContent}>
            <Typography variant="body2" className={classes.muted}>
              Marks only this {humanize(invoice.part)} part as sent. Enter the invoice number from the finance system.
            </Typography>
            <TextField
              variant="outlined"
              size="small"
              label="Finance invoice number"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              inputProps={{ maxLength: 64 }}
              autoFocus
              required
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkOpen(false)} disabled={marking}>Cancel</Button>
          <Button onClick={markSent} color="primary" variant="contained" disableElevation disabled={marking}>
            {marking ? <CircularProgress size={20} /> : "Mark sent"}
          </Button>
        </DialogActions>
      </Dialog>
      <Notification />
    </div>
  );
};

export default InvoiceShow;
